import { decode, JwtPayload } from 'jsonwebtoken';
import { Country } from 'core/models/country';
import { AuthProvider, ConnectLoginRequest, ConnectRefreshResponse, ConnectRequest, ConnectStoreBase, ConnectStoreLoginResponse, ConnectUrlAuthCodeResponse, ConnectUrlResponse, isConnectCustomer, isConnectStoreLoginResponse, SmartcardSurnameResponse, SmartcardSurnameToken, UserAuth } from './auth-types';
import { CachedItem } from '../../../../../../../libs/self-service-core/src/lib/LocalStorageHelper';

export enum ConnectAuthType {
  UrlParameters,
  LegacyConnectStore
}

export const DefaultUserAuth: UserAuth = {
  provider: AuthProvider.Connect,
  isDefault: true,
  isAuthorized: false,
  isAuthenticated: false,
  appEntry: '#/',
  auth: {
    accessToken: null,
    idToken: null,
    expiry: undefined,
  },
  customer: {
    customerNumber: null,
    surname: null,
    country: null,
    countryCode: null,
    mobileNumber: null,
  }
}

export class AuthUtils {
  static GetStoredUser() {
    return new CachedItem('user', true)?.get() as UserAuth;
  }

  static GetStoredToken() {
    return AuthUtils.GetStoredAuth()?.accessToken;
  }

  static GetStoredAuth() {
    return AuthUtils.GetStoredUser()?.auth;
  }

  static GetStoredAuthProvider() {
    return AuthUtils.GetStoredUser()?.provider;
  }

  static GetStoredConnectId() {
    const user = AuthUtils.GetStoredUser();
    return user?.customer && isConnectCustomer(user.customer) ? user?.customer?.connectId : null;
  }

  static GetStoredAgreementId() {
    const user = AuthUtils.GetStoredUser();
    return user?.customer && isConnectCustomer(user.customer) ? user?.customer?.agreementId : null;
  }

  static GetStoredMobileNumber() {
    const user = AuthUtils.GetStoredUser();
    return user?.customer && isConnectCustomer(user.customer) ? user?.customer?.mobileNumber : null;
  }

  static ConnectStore() {
    return new CachedItem('connect-store', true, undefined, false)
  }

  // NOTE: MAH - smartcard/surname login Mapping
  static MapSmartcardSurnameResponse(response: SmartcardSurnameResponse): UserAuth {
    const parsedToken = AuthUtils.ParseJwt(response.access_token) as SmartcardSurnameToken;

    const alpha2CountryCode = Country.GetFromFullName(parsedToken.country)?.alpha2 || null;

    return {
      provider: AuthProvider.Mah,
      isAuthorized: !!(response.access_token),
      isAuthenticated: !!(response.access_token),
      isLinked: !!(parsedToken['customer.number']),
      isDefault: false,
      auth: {
        accessToken: response.access_token,
        expiry: parsedToken.exp,
        tokenType: response.token_type,
      },
      customer: {
        customerNumber: parsedToken['customer.number'],
        country: parsedToken.country,
        countryCode: alpha2CountryCode,
        mobileNumber: `${parsedToken['phone.country.code']}${parsedToken['phone.number']}`,
        smartCardNumber: parsedToken['smartcard.number'],
        surname: parsedToken['surname'],
      },
    };
  }

  // NOTE: Connect Mapping functions->
  static MapConnectUserToUser(connectAuthType: ConnectAuthType, value: ConnectUrlResponse | ConnectUrlAuthCodeResponse | ConnectStoreBase | ConnectStoreLoginResponse): UserAuth {

    if (connectAuthType === ConnectAuthType.UrlParameters) {
      const connectParams = value as ConnectUrlResponse;
      const jwt = connectParams.access_token ? AuthUtils.ParseJwt(connectParams.access_token) : null;
      const { exp } = !!jwt
        ? jwt
        : { exp: undefined };

      return {
        provider: AuthProvider.Connect,
        isAuthorized: !!(connectParams.access_token),
        isAuthenticated: false,
        isDefault: false,
        auth: {
          authCode: connectParams.auth_code,
          accessToken: connectParams.access_token,
          idToken: connectParams.id_token || null,
          expiry: exp,
          isNewSession: true,
          tokenType: connectParams.token_type,
        },
        customer: DefaultUserAuth.customer
      };
    }

    if (connectAuthType === ConnectAuthType.LegacyConnectStore) {
      const connectStore = value as ConnectStoreBase | ConnectStoreLoginResponse;

      const alpha2CountryCode = Country.GetFromThreeCharCode(connectStore.countryCode)?.alpha2 || null;
      const customerNumber = !isConnectStoreLoginResponse(connectStore) ? connectStore.customerNumber : connectStore.CustomerNumber;
      const { exp } = AuthUtils.ParseJwt(connectStore.access_token);

      return {
        provider: AuthProvider.Connect,
        isAuthorized: !!(connectStore.access_token),
        isAuthenticated: !!(connectStore.authenticated && connectStore.connectId),
        isLinked: !!(customerNumber),
        isDefault: false,
        auth: {
          accessToken: connectStore.access_token,
          idToken: connectStore.id_token,
          expiry: exp,
          isNewSession: false,
          tokenType: connectStore.token_type,
        },
        customer: {
          customerNumber,
          country: connectStore.Country,
          countryCode: alpha2CountryCode,
          mobileNumber: connectStore.mobileNumber,
          connectId: connectStore.connectId,
          agreementId: connectStore.agreementId,
          emailAddress: connectStore.email,
        }
      };
    }

    // FIXME: validate this scenario
    return value as any;
  }

  static MapConnectRefreshResponse(value: ConnectRefreshResponse): Partial<UserAuth> {
    const response = value;
    const jwt = AuthUtils.ParseJwt(response.accessToken);
    const { exp } = jwt;

    return {
      provider: AuthProvider.Connect,
      isAuthorized: !!(response.accessToken),
      isDefault: false,
      auth: {
        accessToken: response.accessToken,
        idToken: response.idToken || null,
        expiry: exp,
        isNewSession: false,
      },
    };
  }

  static MapUserToConnectLoginDTO(user: UserAuth): ConnectLoginRequest | null {
    if (!user.auth.accessToken) {
      return null;
    }

    const reauthenticateCustomer = !user.auth.isNewSession;

    return {
      accessToken: user.auth.accessToken,
      idToken: user.auth.idToken || user.auth.accessToken,
      forceReAuth: reauthenticateCustomer,
    }
  }

  static MapUserToConnectRefreshDTO(user: UserAuth): ConnectRequest | null {
    if (!user.auth.accessToken) {
      return null;
    }

    return {
      accessToken: user.auth.accessToken,
      idToken: user.auth.idToken || user.auth.accessToken,
    }
  }

  static CleanUrl() {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    if (code || state || error) {
      window.history.pushState({}, document.title, `${window.location.pathname}${window.location.hash}`);
    }
  }

  static ParseJwt(token: string) {
    return decode(token) as JwtPayload;
  }

  static ParseResponseData<T>(data: string | T): T {
    const dataIsString = typeof (data) === 'string'
    return dataIsString ? JSON.parse(data) : data;
  }
}